import { message, Modal, Upload } from "antd";
import { PlusOutlined, LoadingOutlined } from "@ant-design/icons";
import axios, { AxiosRequestConfig } from "axios";
import { useEffect, useState } from "react";
import ImgCrop from "antd-img-crop";
import { RcFile, UploadFile, UploadProps } from "antd/lib/upload/interface";
import { fileToBase64 } from "../../../utils/functions/file/fileToBase64";
import { mediaUrl } from "../../../utils/functions/media/mediaUrl";

interface ImageUploaderProps extends UploadProps {
  aspect?: number;
  name: string;
  url: string[];
  action: string;
  onComplete: Function;
  maxCount?: number;
  multiple?: boolean;
  placeholder?: string;
  hint?: string;
  // When true, the cropped output is re-encoded as JPEG before being handed
  // off. antd-img-crop runs the file through <canvas>.toBlob(file.type, ...);
  // for PNG inputs the browser's PNG encoder is far less efficient than what
  // image tools produce, so a 3 MB poster routinely balloons to 8-12 MB after
  // the crop. Re-encoding to JPEG keeps poster uploads under the 5 MB cap.
  outputAsJpeg?: boolean;
}

async function reencodeAsJpeg(file: File, quality = 0.9): Promise<File> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error("image load failed"));
      i.src = objectUrl;
    });
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("canvas 2d context unavailable");
    // JPEG has no alpha channel; paint white behind so transparent pixels
    // don't render as black.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);
    const blob: Blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("toBlob failed"))),
        "image/jpeg",
        quality
      );
    });
    const base = file.name.replace(/\.[^.]+$/, "") || "image";
    return new File([blob], `${base}.jpg`, { type: "image/jpeg" });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export const ImageUploader = (props: ImageUploaderProps) => {
  let files: UploadFile[] =
    props.url?.map((u, i) => {
      const url = mediaUrl(u);
      return {
        url,
        status: "done",
        uid: i.toString(),
        name: url.split("/").pop()?.split("?")[0] || "",
      };
    }) || [];
  const [fileList, setFileList] = useState<UploadFile[]>(files);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadLoading, setUploadLoading] = useState<Boolean>(false);

  useEffect(() => {
    setFileList(
      props.url?.map((u, i) => {
        const url = mediaUrl(u);
        return {
          url,
          status: "done",
          uid: i.toString(),
          name: url.split("/").pop()?.split("?")[0] || "",
        };
      }) ?? []
    );
  }, [props.url]);

  const handleCancel = () => setPreviewVisible(false);

  const beforeUpload = (file: File) => {
    const isJpgOrPng =
      ["image/jpeg", "image/png"].indexOf(file.type) > -1 ? true : false;
    if (!isJpgOrPng) {
      message.error("You can only upload JPG/PNG file!");
      console.error("You can only upload JPG/PNG file!");
      return isJpgOrPng || Upload.LIST_IGNORE;
    }
    const isLt2M = file.size / 1024 / 1024 < 10;
    if (!isLt2M) {
      message.error("Image must smaller than 10MB!");
      console.error("Image must smaller than 10MB!");
      return isLt2M || Upload.LIST_IGNORE;
    }
    return true;
  };

  const handleImageChange: UploadProps["onChange"] = ({
    file: newFile,
    fileList: newFileList,
  }) => {
    setFileList(newFileList);
    newFile.percent = uploadProgress;
    if (newFile.status === "uploading") {
      setUploadLoading(true);
      return;
    }
    if (newFile.status === "done") {
      // Get this url from response in real world.
      // const imageURL = await UtilsService.getBase64(newFile.originFileObj as RcFile);
      setUploadLoading(false);
      setTimeout(() => {
        props.onComplete(newFileList);
      }, 200);
    }
  };

  const handleImageUpload = async (options: any) => {
    const { onSuccess, file } = options;
    let finalFile: File = file;
    if (props.outputAsJpeg) {
      try {
        finalFile = await reencodeAsJpeg(file);
      } catch (e) {
        console.error("JPEG re-encode failed; using original file", e);
      }
    }
    const url = URL.createObjectURL(finalFile);

    onSuccess([url, finalFile]);
  };

  const onPreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await fileToBase64(file.originFileObj as RcFile);
    }
    setPreviewImage(file.url || (file.preview as string));
    setPreviewVisible(true);
    setPreviewTitle(
      file.name || file.url!.substring(file.url!.lastIndexOf("/") + 1)
    );
  };

  const uploadButton = (
    <div>
      {uploadLoading ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>{props.placeholder ?? "Upload"}</div>
      {props.hint ? (
        <div style={{ marginTop: 4, fontSize: "1.05rem", opacity: 0.55 }}>
          {props.hint}
        </div>
      ) : null}
    </div>
  );

  return (
    <>
      <ImgCrop aspect={props.aspect || 1} rotationSlider>
        <Upload
          name={props.name}
          listType="picture-card"
          showUploadList={true}
          maxCount={props.maxCount || 1}
          fileList={fileList}
          className="avatar-uploader"
          action={props.action}
          onChange={handleImageChange}
          beforeUpload={beforeUpload}
          onPreview={onPreview}
          customRequest={handleImageUpload}
          onRemove={props.onRemove}
          multiple={props.multiple ?? false}
          // headers={{ "Authorization": "Bearer " + token }}
        >
          {fileList.length >= (props.maxCount || 1) ? null : uploadButton}
        </Upload>
      </ImgCrop>
      <Modal
        visible={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={handleCancel}
      >
        <img alt="example" style={{ width: "100%" }} src={previewImage} />
      </Modal>
    </>
  );
};
